// The composer: live segment counter, one idempotency key per draft, Enter sends,
// quiet-hours notice with an optional "Send now", locked when the number opted out.
// Phase 2 adds three tools left of the counter: Template, Attach picture, AI. All three only
// fill the box or attach a chip; the one Send path below is still the only way out.

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { countSegments, describeUcs2Triggers, plainText } from '@bt/core';
import { absoluteUrl, api, errorMessage, type AiSuggestion, type Contact, type MeResponse, type OptOut, type PolicyResponse } from '../../api';
import { clearKey, getOrMintKey, loadDraft, saveDraft } from '../../lib/idempotency';
import { fmtTime, uuid, zoneLabel } from '../../lib/format';
import { lockedComposerText } from '../../lib/words';
import { Dialog } from '../../components/Dialog';
import { TemplateMenu } from '../../components/TemplatePicker';
import { PicturePickerDialog, pictureOf, usePictures, type PickedPicture } from '../../components/PicturePicker';
import { AiPanel, type AiChoice } from './AiPanel';

export function Composer({
  conversationId,
  me,
  contact,
  optedOut,
  optOut,
  contactTz,
  hasInbound,
  prefill,
}: {
  conversationId: string;
  me: MeResponse;
  contact: Contact;
  optedOut: boolean;
  optOut: OptOut | null;
  contactTz: string;
  hasInbound: boolean;
  /** text handed over by the New text dialog (a rendered template) */
  prefill?: string | null;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState(() => loadDraft(conversationId));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [policy, setPolicy] = useState<PolicyResponse | null>(null);
  const [deferredBody, setDeferredBody] = useState<string | null>(null);
  // the row the gate deferred to the next window; "Send now" cancels it so the text never goes out twice
  const [deferredMessageId, setDeferredMessageId] = useState<string | null>(null);
  const [askReason, setAskReason] = useState(false);
  const [reason, setReason] = useState('');
  // one key per opening of the Send now dialog, so a retry after a lost answer is the same message
  const overrideKeyRef = useRef<string | null>(null);
  const [attachment, setAttachment] = useState<PickedPicture | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showPictures, setShowPictures] = useState(false);
  const [showAi, setShowAi] = useState(false);
  // set by "Use" on an AI card; reported to the server after the text goes out
  const [aiChoice, setAiChoice] = useState<AiChoice | null>(null);
  const keyRef = useRef<string | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);

  const templates = useQuery({ queryKey: ['templates'], queryFn: api.templates.list, staleTime: 30_000, enabled: showTemplates });
  const pictures = usePictures();

  useEffect(() => {
    if (text) keyRef.current = getOrMintKey(conversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // a rendered template from the New text dialog lands in the box once
  useEffect(() => {
    if (prefill && prefill.trim()) fill(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // close the template menu on an outside click
  useEffect(() => {
    if (!showTemplates) return;
    const onDown = () => setShowTemplates(false);
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [showTemplates]);

  const onChange = (v: string) => {
    setText(v);
    saveDraft(conversationId, v);
    if (v && !keyRef.current) keyRef.current = getOrMintKey(conversationId);
    if (error) setError(null);
  };

  /** put text in the box (template or AI "Use"); the person can still edit it */
  const fill = (v: string) => {
    onChange(v);
    window.setTimeout(() => areaRef.current?.focus(), 0);
  };

  const send = async (opts: { override?: { reason: string }; body?: string; attachment?: PickedPicture | null; aiChoice?: AiChoice | null; key?: string } = {}) => {
    const body = (opts.body ?? text).trim();
    const attach = opts.attachment === undefined ? attachment : opts.attachment;
    const choice = opts.aiChoice === undefined ? aiChoice : opts.aiChoice;
    if (!body || sending) return;
    // An override and an AI "Send" are each their own message, so neither reuses the typed draft's
    // key. WHY they must not mint a fresh one per click (review round 2): when the request reaches
    // the server but the answer is lost, the button comes back and the second click used to post
    // the same text under a NEW key — two rows, two texts. The caller passes a key that is stable
    // for that card / that dialog, so a retry collapses onto the first row exactly like the draft.
    const key = opts.key ?? (opts.override || opts.body !== undefined ? uuid() : (keyRef.current ?? getOrMintKey(conversationId)));
    setSending(true);
    setError(null);
    try {
      if (opts.override && deferredMessageId) {
        // WHY (review round 1, trap 8): the deferred copy would leave again at the next window open.
        // WHY the check (review round 2): a 409 means it is already sending or on the phone, and
        // sending the override on top of it texts the customer twice.
        const cancelled = await api.conversations.cancelMessage(conversationId, deferredMessageId).then(() => true, () => false);
        if (!cancelled) {
          setError('The earlier copy has already started sending, so nothing was sent now. Check the thread.');
          return;
        }
        setDeferredMessageId(null);
      }
      const res = await api.conversations.send(conversationId, {
        body,
        idempotency_key: key,
        override: opts.override ?? null,
        // WHY: the phone fetches the picture by URL, so it must be absolute (DESIGN §12.3)
        attachments: attach ? [{ url: absoluteUrl(attach.url), contentType: attach.contentType }] : undefined,
      });
      setPolicy(res.policy);
      if (res.policy?.decision === 'defer' && !opts.override) {
        setDeferredBody(body);
        setDeferredMessageId(res.message?.id ?? null);
      } else {
        setDeferredBody(null);
        setDeferredMessageId(null);
      }
      if (choice) {
        // best effort: the text went out either way
        api.ai.choose(choice.id, { index: choice.index, message_id: res.message?.id ?? null }).catch(() => {});
      }
      // a typed send clears the draft; an AI "Send" or an override leaves the typed draft (and its chip) alone
      if (!opts.override && opts.body === undefined) {
        clearKey(conversationId);
        keyRef.current = null;
        setText('');
        saveDraft(conversationId, '');
        setAttachment(null);
        setAiChoice(null);
      }
      qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
      areaRef.current?.focus();
    } catch (e) {
      setError(errorMessage(e)); // the draft and the key stay, so a retry reuses the same key
      throw e;
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send().catch(() => {});
    }
  };

  /** AI "Use": the text (and the picture, if any) go into the composer, editable */
  const useSuggestion = (s: AiSuggestion, choice: AiChoice, thumbUrl: string | null) => {
    fill(s.text);
    setAiChoice(choice);
    if (s.attach_asset_id && thumbUrl) {
      const item = pictures.data?.items.find((i) => i.asset_id === s.attach_asset_id);
      setAttachment(item ? pictureOf(item) : { asset_id: s.attach_asset_id, url: thumbUrl, title: 'picture' });
    }
    setShowAi(false);
  };

  /** AI "Send": straight through the normal send path, then tell the server which card it was */
  const sendSuggestion = async (s: AiSuggestion, choice: AiChoice, thumbUrl: string | null) => {
    let attach: PickedPicture | null = null;
    if (s.attach_asset_id && thumbUrl) {
      const item = pictures.data?.items.find((i) => i.asset_id === s.attach_asset_id);
      attach = item ? pictureOf(item) : { asset_id: s.attach_asset_id, url: thumbUrl, title: 'picture' };
    }
    try {
      await send({ body: s.text, attachment: attach, aiChoice: choice, key: `ai:${choice.id}:${choice.index}` });
      setShowAi(false);
    } catch {
      /* the error line under the box says what happened */
    }
  };

  if (optedOut) {
    return (
      <div className="composer">
        <div className="locked">{lockedComposerText(contact, optOut, me.workspace.timezone)}</div>
      </div>
    );
  }

  const info = countSegments(text);
  const counterClass = info.segments >= 3 ? 'red' : info.segments === 2 ? 'amber' : '';
  // names hidden characters too ('a hidden no-break space after "Call"'), review round 1, trap 5
  const ucs2Reason = info.encoding === 'ucs2' && info.ucs2Triggers.length > 0 ? describeUcs2Triggers(text, info.ucs2Triggers) : null;
  const canPlain = info.encoding === 'ucs2' && plainText(text) !== text;

  const notBefore = policy?.not_before ?? policy?.notBefore ?? null;
  const showDefer = policy?.decision === 'defer' && !!notBefore && new Date(notBefore).getTime() > Date.now();
  const zoneWord = contactTz !== me.workspace.timezone ? ` ${zoneLabel(contactTz)}` : '';
  const repetition = policy?.warnings?.includes('repetition_15');
  // WHY (review round 2): the gate counts the text it actually stored and warns segments_N; the
  // composer read only repetition and defer, so an AI or template send never showed its own count.
  const sentSegments = Number(/^segments_(\d+)$/.exec(policy?.warnings?.find((w) => w.startsWith('segments_')) ?? '')?.[1] ?? 0);
  const blocked = policy?.decision === 'block';
  const templateVars: Record<string, string> = { company: me.workspace.company_name ?? me.workspace.name, name: contact.name?.trim() ?? '' };
  const aiOn = me.workspace.ai_enabled !== false;

  return (
    <div className="composer">
      {!hasInbound && (
        <div className="hint">
          Say who you are: “Hi, this is {me.user.name} at {me.workspace.company_name ?? me.workspace.name}”
        </div>
      )}
      {showDefer && (
        <div className="notice warn" style={{ marginBottom: 6 }}>
          Outside texting hours for this customer ({fmtTime(new Date().toISOString(), contactTz)}{zoneWord}). Will send at {fmtTime(notBefore, contactTz)}.
          {me.workspace.allow_quiet_hours_override && deferredBody && (
            <>
              {' '}
              <button
                className="btn link small"
                onClick={() => {
                  overrideKeyRef.current = overrideKeyRef.current ?? uuid();
                  setAskReason(true);
                }}
              >
                Send now
              </button>
            </>
          )}
        </div>
      )}
      {blocked && (
        <div className="notice danger" style={{ marginBottom: 6 }}>
          {blockedNotice(policy?.reason)}
        </div>
      )}
      {repetition && (
        <div className="notice warn" style={{ marginBottom: 6 }}>
          This same text has gone out 15 or more times today. Vary the words, or the carrier will read it as automated.
        </div>
      )}
      {sentSegments >= 2 && (
        <div className={`notice ${sentSegments >= 3 ? 'danger' : 'warn'}`} style={{ marginBottom: 6 }}>
          That text went out as {sentSegments} segments, so it costs {sentSegments}x a normal one.
        </div>
      )}
      {policy?.warnings?.includes('quiet_hours_reply_exception') && (
        <div className="hint">Sent outside texting hours because the customer wrote first today.</div>
      )}
      {attachment && (
        <div className="attach-chip" title={attachment.title}>
          <img src={attachment.url} alt="" />
          <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {attachment.title}
          </span>
          <button className="x" onClick={() => setAttachment(null)} aria-label="Remove picture" title="Remove">
            ×
          </button>
        </div>
      )}
      <textarea
        ref={areaRef}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKey}
        placeholder="Type a text. Enter sends, Shift+Enter for a new line."
        disabled={sending}
        rows={3}
      />
      {showAi && <AiPanel conversationId={conversationId} busy={sending} onUse={useSuggestion} onSend={sendSuggestion} onClose={() => setShowAi(false)} />}
      <div className="foot">
        <div className="tools">
          <button className="btn" type="button" onClick={() => setShowTemplates((v) => !v)} onMouseDown={(e) => e.stopPropagation()} disabled={sending}>
            Template
          </button>
          <button className="btn" type="button" onClick={() => setShowPictures(true)} disabled={sending}>
            Attach picture
          </button>
          <button className="btn" type="button" onClick={() => setShowAi((v) => !v)} disabled={sending || !aiOn} title={aiOn ? 'Ask the AI for a reply' : 'The AI helper is switched off in Settings › AI'}>
            AI
          </button>
          <span className={`counter ${counterClass}`}>
            {info.length} / {info.capacity} · {info.segments <= 1 ? 'counts as 1 text' : `counts as ${info.segments} texts`}
          </span>
          {ucs2Reason && <span className="warn-text"> · {ucs2Reason}</span>}
          {canPlain && (
            <button className="btn link small" type="button" onClick={() => onChange(plainText(text))} title="Straight quotes, plain spaces, no hidden characters">
              Make plain text
            </button>
          )}
          {info.segments >= 3 && <span className="danger-text"> · this long it costs more and can arrive out of order</span>}
          {showTemplates && (
            <TemplateMenu
              templates={templates.data?.items ?? []}
              loading={templates.isPending}
              vars={templateVars}
              onFill={(t) => {
                fill(t);
                setShowTemplates(false);
              }}
              onClose={() => setShowTemplates(false)}
            />
          )}
        </div>
        <button className="btn primary" disabled={sending || !text.trim()} onClick={() => send().catch(() => {})}>
          {sending ? 'Sending' : 'Send'}
        </button>
      </div>
      {error && (
        <div className="danger-text small" role="alert" style={{ marginTop: 4 }}>
          {error}{' '}
          <button className="btn link small" onClick={() => send().catch(() => {})}>
            Try again
          </button>
        </div>
      )}
      {showPictures && <PicturePickerDialog onPick={(p) => setAttachment(p)} onClose={() => setShowPictures(false)} />}
      {askReason && (
        <Dialog title="Send now, during quiet hours" onClose={() => setAskReason(false)}>
          <p className="muted">Give a reason. It is kept with the message.</p>
          <textarea className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. customer asked us to confirm tonight" autoFocus />
          <div className="actions">
            <button className="btn" onClick={() => setAskReason(false)}>
              Cancel
            </button>
            <button
              className="btn primary"
              disabled={reason.trim().length < 3 || sending}
              onClick={() => {
                setAskReason(false);
                const overrideKey = overrideKeyRef.current ?? uuid();
                overrideKeyRef.current = overrideKey;
                send({ override: { reason: reason.trim() }, body: deferredBody ?? undefined, attachment: null, aiChoice: null, key: overrideKey })
                  .then(() => {
                    overrideKeyRef.current = null; // a 2xx retires the key, exactly like the draft's
                  })
                  .catch(() => {});
                setReason('');
              }}
            >
              Send now
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function blockedNotice(reason: string | undefined): string {
  switch (reason) {
    case 'opted_out': return 'Not sent: this number has opted out.';
    case 'repetition': return 'Not sent: this same text has gone out too many times today. Change the wording.';
    case 'line_paused': return 'Not sent: the texting line is paused. See Health.';
    case 'line_inactive': return 'Not sent: the texting line is switched off. See Settings › Lines.';
    case 'company_name_missing': return 'Not sent: set the company name in Settings first.';
    case 'quiet_hours': return 'Not sent: outside texting hours.';
    default: return 'Not sent.';
  }
}
